import { Link } from 'react-router-dom';
import { mediaList } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';
import type { ReactNode } from 'react';

interface MediaCardProps {
  id: string;
  layout?: 'grid' | 'row';
  index?: number;
}

export default function MediaCard({ id, layout = 'grid', index = 0 }: MediaCardProps) {
  const item = mediaList.find((media) => media.id === id);
  const { isFavorite, toggle } = useFavorites();
  if (!item) return null;

  const favorite = isFavorite(item.id);
  const heart = (
    <button
      type="button"
      aria-label={favorite ? '取消收藏' : '加入收藏'}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        toggle(item.id);
      }}
      className={`flex h-7 w-7 items-center justify-center rounded-full backdrop-blur transition ${
        favorite ? 'bg-secondary-500 text-background-50' : 'bg-background-950/40 text-background-50 hover:bg-background-950/65'
      }`}
    >
      <i className={`${favorite ? 'ri-heart-3-fill' : 'ri-heart-3-line'} text-xs`} />
    </button>
  );

  if (layout === 'row') {
    return (
      <Link
        to={`/detail/${item.id}`}
        data-product-shop
        className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition hover:border-primary-300 hover:bg-primary-50/40"
      >
        <div className="h-[104px] w-[74px] shrink-0 overflow-hidden rounded-md bg-background-200">
          <img src={item.poster} alt={`${item.title} 海报`} title={`${item.title} 影视推荐`} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-2">
            <h3 className="truncate text-sm font-medium text-foreground-950 group-hover:text-primary-700">{item.title}</h3>
            <span className="shrink-0 rounded bg-accent-100 px-1.5 py-0.5 text-[11px] font-medium text-accent-900">{item.rating.toFixed(1)}</span>
          </div>
          <p className="mt-1 text-[11px] text-foreground-500">
            {item.type} · {item.region} · {item.year}
          </p>
          <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600">{item.summary}</p>
          <div className="mt-auto flex flex-wrap gap-1 pt-2">
            {item.genres.map((genre) => (
              <span key={genre} className="rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-900">{genre}</span>
            ))}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${item.id}`}
      data-product-shop
      style={{ animationDelay: `${Math.min(index, 8) * 45}ms` }}
      className="group animate-fade-up overflow-hidden rounded-lg border border-background-200 bg-background-50 transition hover:-translate-y-1 hover:border-primary-300"
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-background-200">
        <img
          src={item.poster}
          alt={`${item.title} 海报`}
          title={`${item.title} 影视推荐`}
          className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.06]"
        />
        <div className="absolute left-2 top-2 flex items-center gap-1 rounded-md bg-background-950/70 px-1.5 py-0.5 text-[11px] font-medium text-accent-300">
          <i className="ri-star-fill w-3 h-3 flex items-center justify-center text-[10px]" />
          {item.rating.toFixed(1)}
        </div>
        <div className="absolute right-2 top-2">{heart}</div>
        <div className="absolute inset-x-0 bottom-0 translate-y-full bg-gradient-to-t from-background-950/90 to-transparent px-3 pb-3 pt-8 transition-transform duration-300 group-hover:translate-y-0">
          <p className="line-clamp-2 text-[11px] leading-relaxed text-background-100/90">{item.tagline}</p>
        </div>
      </div>
      <div className="p-3">
        <h3 className="truncate text-sm font-medium text-foreground-950 transition group-hover:text-primary-700">{item.title}</h3>
        <p className="mt-1 truncate text-[11px] text-foreground-500">
          {item.year} · {item.region} · {item.type}
        </p>
        <div className="mt-2 flex flex-wrap gap-1">
          {item.genres.map((genre) => (
            <span key={genre} className="rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-900">{genre}</span>
          ))}
        </div>
      </div>
    </Link>
  );
}

export function SectionTitle({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
      <div className="flex items-end gap-3">
        <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">{title}</h2>
        {subtitle && <p className="pb-1 text-xs text-foreground-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}